import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Box, IconButton, Tab, Typography } from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { EditOutlined } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Spinner from "@accredo/fe-shared/components/Spinner.jsx";
import { useBreadcrumb } from "@accredo/fe-shared/contexts/BreadcrumbContext.jsx";
import {
  fetchClaimForm,
  updateClaimForm,
} from "@accredo/fe-shared/services/claimFormService.js";
import { useAbility } from "../../../hooks/permissions/useAbility.jsx";
import { getClaimFormIcon } from "../../../utils/claimFormIcons.js";
import {
  ContentDialog,
  ContentDialogInit,
} from "components/MDDForm/components/ContentDialog.jsx";
import Form from "@accredo/fe-shared/components/Form/Form.jsx";
import FormTextField from "../../../components/Form/controls/FormTextField.jsx";
import SubmitButton from "@accredo/fe-shared/components/Form/controls/SubmitButton.jsx";
import ClaimFormOverviewTab from "./components/ClaimFormOverviewTab.jsx";
import ClaimFormHistoryTab from "./components/ClaimFormHistoryTab.jsx";
import ClaimFormSpartenTab from "./components/ClaimFormSpartenTab.jsx";

const TABS = [
  { value: "overview", label: "Übersicht" },
  { value: "history", label: "Verlauf" },
  { value: "sparten", label: "Sparten" },
];

export default function ClaimFormShowPage() {
  const { id, tab } = useParams();
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();
  const { can } = useAbility();
  const { openCD, setOpenCD, tca, setTca, CDActions } = ContentDialogInit();

  const [claimForm, setClaimForm] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const activeTab = TABS.find((t) => t.value === tab)?.value ?? "overview";

  const load = useCallback(() => {
    setIsLoading(true);
    return fetchClaimForm(id)
      .then((res) => setClaimForm(res?.data ?? null))
      .catch(() =>
        enqueueSnackbar("Schadenformular konnte nicht geladen werden", {
          variant: "error",
        })
      )
      .finally(() => setIsLoading(false));
  }, [id, enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setBreadcrumbs([
      { name: "Home", path: "/home" },
      { name: "Einstellungen", path: "/admin/settings" },
      { name: "Schadenformulare", path: "/admin/claim-forms" },
      {
        name: claimForm?.label ?? `Formular #${id}`,
        path: `/admin/claim-forms/${id}`,
      },
    ]);
  }, [setBreadcrumbs, claimForm, id]);

  const closeDialog = () => setOpenCD(false);

  const openEditModal = () => {
    const validationSchema = yup.object({
      label: yup.string().required("Name ist erforderlich"),
    });

    setOpenCD(true);
    setTca({
      title: "Schadenformular bearbeiten",
      content: (
        <Form
          defaultValues={{
            label: claimForm.label,
            icon_key: claimForm.icon_key ?? "",
            description: claimForm.description ?? "",
          }}
          onSubmit={async (formData) => {
            try {
              await updateClaimForm(id, formData);
              closeDialog();
              load();
              return true;
            } catch (error) {
              console.error("Error updating claim form:", error);
              return false;
            }
          }}
          resolver={yupResolver(validationSchema)}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              minWidth: 400,
            }}
          >
            <FormTextField name="label" label="Name" required fullWidth />
            <FormTextField name="icon_key" label="Icon-Key" fullWidth />
            <FormTextField
              name="description"
              label="Beschreibung"
              fullWidth
              multiline
              minRows={2}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <SubmitButton variant="primary">Speichern</SubmitButton>
            </Box>
          </Box>
        </Form>
      ),
      actions: null,
    });
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (!claimForm) {
    return (
      <Box>
        <Typography variant="h5">Schadenformular nicht gefunden</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="h1" gutterBottom>
            {claimForm.label}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            Icon: {claimForm.icon_key ?? "–"}
            {claimForm.icon_key &&
              (() => {
                const Icon = getClaimFormIcon(claimForm.icon_key);
                return <Icon fontSize="small" />;
              })()}
          </Typography>
          {claimForm.description && (
            <Typography variant="body1" color="text.secondary">
              {claimForm.description}
            </Typography>
          )}
        </Box>
        {can("ClaimForm", "update") && (
          <IconButton onClick={openEditModal} aria-label="Bearbeiten">
            <EditOutlined />
          </IconButton>
        )}
      </Box>

      <TabContext value={activeTab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList
            onChange={(_, v) => navigate(`/admin/claim-forms/${id}/${v}`)}
          >
            {TABS.map((t) => (
              <Tab key={t.value} label={t.label} value={t.value} />
            ))}
          </TabList>
        </Box>

        <TabPanel value="overview" sx={{ px: 0 }}>
          <ClaimFormOverviewTab
            claimForm={claimForm}
            canEditDefinition={can("ClaimForm", "update")}
            onReload={load}
          />
        </TabPanel>
        <TabPanel value="history" sx={{ px: 0 }}>
          <ClaimFormHistoryTab claimFormId={id} />
        </TabPanel>
        <TabPanel value="sparten" sx={{ px: 0 }}>
          <ClaimFormSpartenTab claimForm={claimForm} onReload={load} />
        </TabPanel>
      </TabContext>

      <ContentDialog
        openCD={openCD}
        setOpenCD={setOpenCD}
        CDActions={CDActions}
        tca={tca}
      />
    </>
  );
}
